import { useEffect, useRef, useCallback } from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import logger from 'itrvl-logger';
import { useUser } from 'common/context/UserContext';
import { useApi } from 'common/hooks/api';
import {
  DRAWER_BODY_CLASS_COLLAPSED,
  DRAWER_BODY_CLASS_EXPANDED,
  DRAWER_HEIGHT_COLLAPSED,
  DRAWER_HEIGHT_EXPANDED,
} from 'pages/ItineraryBuilder/constants';

const log = logger(__filename);

const FEATUREBASE_LAUNCHER_HEIGHT = 64;
const LAUNCHER_SELECTOR = '.featurebase-messenger-launcher-frame-wrapper';
const NOTIFICATION_SELECTOR = '.featurebase-notification-frame';
const WIDGET_SELECTOR = '.featurebase-messenger-frame-wrapper';
const ALL_FRAME_SELECTORS = `${LAUNCHER_SELECTOR}, ${NOTIFICATION_SELECTOR}, ${WIDGET_SELECTOR}`;

function drawerSelectors(drawerClass, ...selectors) {
  return selectors.map(s => `body.${drawerClass} ${s}`).join(', ');
}

const featurebaseStyles = {
  '@keyframes featurebase-fade-in': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  [ALL_FRAME_SELECTORS]: {
    zIndex: '1199 !important',
    filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4)) !important',
    animation: 'featurebase-fade-in 400ms ease-out both !important',
    transition: 'bottom 225ms ease !important',
  },
  [drawerSelectors(DRAWER_BODY_CLASS_COLLAPSED, LAUNCHER_SELECTOR, NOTIFICATION_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_COLLAPSED}px !important`,
  },
  [drawerSelectors(DRAWER_BODY_CLASS_COLLAPSED, WIDGET_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_COLLAPSED + FEATUREBASE_LAUNCHER_HEIGHT}px !important`,
  },
  [drawerSelectors(DRAWER_BODY_CLASS_EXPANDED, LAUNCHER_SELECTOR, NOTIFICATION_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_EXPANDED}px !important`,
  },
  [drawerSelectors(DRAWER_BODY_CLASS_EXPANDED, WIDGET_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_EXPANDED + FEATUREBASE_LAUNCHER_HEIGHT}px !important`,
  },
};

function useUnreadTitleAlert() {
  const unreadCount = useRef(0);

  const updateTitle = useCallback(count => {
    if (count > 0) {
      const base = document.title.replace(/^\(\d+\)\s*/, '');
      document.title = `(${count}) ${base}`;
    } else {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    }
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      updateTitle(unreadCount.current);
    };

    const onMessage = event => {
      if (!event.origin.endsWith('.featurebase.app')) return;
      const data = event.data;
      if (data?.type === 'UNREAD_COUNT_CHANGE') {
        const count = data.unreadCount ?? 0;
        unreadCount.current = count;
        updateTitle(count);
      }
      if (data?.type === 'NOTIFICATION_AVAILABLE') {
        unreadCount.current = Math.max(unreadCount.current, 1);
        updateTitle(unreadCount.current);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('message', onMessage);
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [updateTitle]);
}

function loadSdk() {
  const id = 'featurebase-sdk';
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.src = 'https://do.featurebase.app/js/sdk.js';
  document.head.appendChild(s);
  if (typeof window.Featurebase !== 'function') {
    window.Featurebase = function () {
      (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
    };
  }
}

function Featurebase() {
  const { user } = useUser();
  const Api = useApi();
  const hasBooted = useRef(false);
  const bootIdRef = useRef(0);
  useUnreadTitleAlert();

  const shutdown = useCallback(() => {
    bootIdRef.current++;
    if (hasBooted.current) {
      window.Featurebase('shutdown');
      hasBooted.current = false;
    }
  }, []);

  const bootAuthenticated = useCallback(
    async currentBootId => {
      try {
        if (currentBootId !== bootIdRef.current) return;
        const { data } = await Api.get('/api/Info/featurebaseToken');
        const { featurebaseJwt } = data;

        window.Featurebase('boot', {
          appId: process.env.REACT_APP_FEATUREBASE_APP_ID,
          featurebaseJwt,
          theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
          language: 'en',
        });
        hasBooted.current = true;
      } catch (err) {
        if (currentBootId !== bootIdRef.current) return;
        log.warn('[featurebase] JWT fetch failed', err);
      }
    },
    [Api],
  );

  useEffect(() => {
    loadSdk();
  }, []);

  useEffect(() => {
    shutdown();

    if (user?.userId) {
      const currentBootId = ++bootIdRef.current;
      bootAuthenticated(currentBootId);
    }

    return shutdown;
  }, [user?.userId, shutdown, bootAuthenticated]);

  return <GlobalStyles styles={featurebaseStyles} />;
}

export default Featurebase;
